import React, { Component } from 'react'
import { Button, Menu, Popover, Table, Icon, Tag, Tooltip } from 'antd'
import { Dropdown } from 'antd-v4'
import { ColumnProps } from 'antd/lib/table'
import { ProductModels, UnitModels } from '@getgreenline/products'
import { ComplianceBadge } from './ComplianceBadge'
import { SmartImage } from '../../../../components/SmartImage'
import Highlighter from 'react-highlight-words'
import { ProductFilter } from './ProductFilter'
import { browserHistory, Link } from 'react-router'
import { Routes } from '../../../../constants/Routes'
import { Currency } from '../../../../components/Currency'
import { PrintProductCheck } from './PrintProductCheck'
import { IExtraFields } from '../../../../utilities/LocalStorage'
import moment from 'moment'
import { ToggleModalType } from './ProductList'
import { EditMenuItem } from './EditMenuItem'
import { ProductStatusType } from '.'
import { inject } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { CannabinoidMetaData, CannabinoidMetaDataItem } from '../AddProduct/ProductStore'
import { isSystemProduct } from '../../../../utilities/helpers'

interface Props extends InjectedProps {
  products: ProductModels.INestedProduct[]
  productFilter: ProductFilter
  canEdit: boolean
  toggleChildProducts: (product: ProductModels.INestedProduct) => void
  duplicate: (product: ProductModels.INestedProduct) => void
  isDuplicating?: boolean
  toggleModal: (type: ToggleModalType, product: ProductModels.INestedProduct) => void
  extraFields: IExtraFields
  showDeleteModal: boolean
  productStatus: ProductStatusType
  undelete: (product: ProductModels.INestedProduct) => void
}

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
}

interface ProductListTableRecord {
  key: string
  imageUrl: JSX.Element
  name: JSX.Element | undefined
  sku: JSX.Element
  categoryName: string | null
  complianceCategoryName: JSX.Element | null
  price: JSX.Element | false
  taxGroupName: string | null
  options: JSX.Element | false
  supplierName: JSX.Element
  weight: JSX.Element
  cannabisWeight: JSX.Element
  cannabisVolume: JSX.Element
  thc: JSX.Element
  cbd: JSX.Element
  barcode: JSX.Element
  createDate: JSX.Element
  updateDate: JSX.Element
  purchasePrice: JSX.Element
  isMasterProduct: boolean
  companyId: number
  parentProductId: string | null
  id: string
  isDeleted: boolean
}

@inject('currentCompanyStore')
export class ProductListTable extends Component<Props> {
  IMAGE_WIDTH = 50

  stylizedProductName = (product: ProductModels.INestedProduct) => {
    const { productFilter } = this.props
    const searchQuery = productFilter.productFilter.searchQuery

    return product.name ? (
      <Highlighter
        highlightClassName='highlighted-text'
        searchWords={[searchQuery]}
        autoEscape
        textToHighlight={product.name}
        style={{ paddingRight: this.props.productStatus === 'deleted' ? 5 : 0 }}
      />
    ) : (
      <span>-</span>
    )
  }

  nameLink = (product: ProductModels.INestedProduct) => {
    if (!this.props.canEdit || isSystemProduct(product)) {
      return <span>{this.stylizedProductName(product)}</span>
    } else {
      return (
        <Link to={Routes.EDIT_PRODUCT(product.companyId, product.id, window.location.search)}>
          {this.stylizedProductName(product)}
        </Link>
      )
    }
  }

  productStatusTag = (product: ProductModels.INestedProduct) => {
    switch (true) {
      case product.isDeleted:
        return <Tag color='red'>Deleted</Tag>
      case product.isActive:
        return <Tag color='blue'>Active</Tag>
      default:
        return <Tag>Inactive</Tag>
    }
  }

  nameSection = (product: ProductModels.INestedProduct) => {
    if (product.parentProductId) {
      return (
        <div style={{ paddingLeft: 16 }}>
          {this.props.productStatus === 'deleted' ? (
            <>
              {this.props.canEdit ? this.nameLink(product) : this.stylizedProductName(product)}
              {this.productStatusTag(product)}
            </>
          ) : (
            this.stylizedProductName(product)
          )}
        </div>
      )
    } else if (product.isMasterProduct) {
      return (
        <div>
          {this.nameLink(product)}
          {this.props.productStatus === 'deleted' && this.productStatusTag(product)}
          <div
            className='blaze-text-muted'
            style={{ paddingTop: 5, paddingBottom: 5 }}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation()
              this.props.toggleChildProducts(product)
            }}
          >
            {product.childProducts.length} variants
            {this.props.productStatus === 'deleted' && ' deleted'}
            <span className='ml-2 fa fa-angle-down' />
          </div>
        </div>
      )
    } else {
      return (
        <div>
          {this.nameLink(product)}
          {this.props.productStatus === 'deleted' && this.productStatusTag(product)}
        </div>
      )
    }
  }

  stylizedSku = (productSku: string | null) => {
    const { productFilter } = this.props
    const searchQuery = productFilter.productFilter.searchQuery

    return productSku ? (
      <Highlighter
        highlightClassName='highlighted-text'
        searchWords={[searchQuery]}
        autoEscape
        textToHighlight={productSku}
      />
    ) : (
      <span />
    )
  }

  getTHCFormatted = (product: ProductModels.INestedProduct) => {
    const { thc, maxTHC, minTHC, unit, showMaxMin } = product.metaData

    if (showMaxMin === 'true') {
      if (minTHC && maxTHC) {
        return `${minTHC}${unit || UnitModels.ThcCbdUnit.PERCENT} - ${maxTHC}${
          unit || UnitModels.ThcCbdUnit.PERCENT
        }`
      } else {
        return null
      }
    } else {
      if (thc) {
        return `${thc}${unit || UnitModels.ThcCbdUnit.PERCENT}`
      } else {
        return null
      }
    }
  }

  getCBDFormatted = (product: ProductModels.INestedProduct) => {
    const { cbd, maxCBD, minCBD, unit, showMaxMin } = product.metaData

    if (showMaxMin === 'true') {
      if (minCBD && maxCBD) {
        return `${minCBD}${unit || UnitModels.ThcCbdUnit.PERCENT} - ${maxCBD}${
          unit || UnitModels.ThcCbdUnit.PERCENT
        }`
      } else {
        return null
      }
    } else {
      if (cbd) {
        return `${cbd}${unit || UnitModels.ThcCbdUnit.PERCENT}`
      } else {
        return null
      }
    }
  }

  getTHCorCBDFormatted = (product: ProductModels.INestedProduct, type: string) => {
    const { thc, cbd, maxTHC, minTHC, maxCBD, minCBD, unit, showMaxMin } = product.metaData

    if (showMaxMin === 'true') {
      if (type === 'thc' && minTHC && maxTHC) {
        return this.getTHCFormatted(product)
      } else if (type === 'cbd' && minCBD && maxCBD) {
        return this.getCBDFormatted(product)
      } else {
        return null
      }
    } else {
      if (type === 'cbd' && cbd) {
        return this.getCBDFormatted(product)
      } else if (type === 'thc' && thc) {
        return this.getTHCFormatted(product)
      } else {
        return null
      }
    }
  }

  getProductRow = (product: ProductModels.INestedProduct) => {
    const cannabinoidMetaData = new CannabinoidMetaData()

    product.cannabinoidMetaData.forEach((cnb) => {
      const cnbItem = new CannabinoidMetaDataItem()
      cnbItem.populate(cnb, product.metaData)
      cannabinoidMetaData.cannabinoids.push(cnbItem)
    })

    const { locationMappedCannabinoidMetaData: mappedCannabinoidMetaData } = cannabinoidMetaData

    return {
      key: product.id,
      imageUrl: (
        <SmartImage src={product.imageUrl} width={this.IMAGE_WIDTH} height={this.IMAGE_WIDTH} />
      ),
      name: this.nameSection(product),
      sku: this.stylizedSku(product.sku),
      categoryName: product.categoryName ? product.categoryName : '-',
      complianceCategoryName: <ComplianceBadge product={product} />,
      price: product.isMasterProduct ? (
        <></>
      ) : (
        <>
          <Currency centValue={product.price} />

          {product.overridePrices && product.overridePrices.length > 0 && (
            <div className='blaze-text-muted' style={{ cursor: 'pointer' }}>
              <Popover
                title='Prices'
                placement='right'
                content={
                  <table className='w-100'>
                    <tbody>
                      <tr>
                        <td>Default</td>
                        <td className='text-right'>
                          <Currency centValue={product.price} />
                        </td>
                      </tr>
                      {product.overridePrices.map((p) => (
                        <tr key={p.id}>
                          <td>{p.locationName}</td>
                          <td className='text-right'>
                            <Currency centValue={p.retailPrice} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                }
              >
                <span>+{product.overridePrices.length} more</span>
              </Popover>
            </div>
          )}
        </>
      ),
      taxGroupName: product.taxGroupName ? product.taxGroupName : '-',
      options: isSystemProduct(product) ? (
        <></>
      ) : (
        <div onClick={(e) => e.stopPropagation()}>
          <Dropdown
            trigger={['click']}
            getPopupContainer={() => document.body}
            overlay={
              product.isDeleted ? (
                <Menu>
                  {this.props.canEdit ? (
                    <Menu.Item
                      onClick={() => this.props.undelete(product)}
                      className='blaze-text-danger'
                    >
                      Undelete
                    </Menu.Item>
                  ) : (
                    <Menu.Item disabled>
                      <Icon type='stop' /> No edit permissions
                    </Menu.Item>
                  )}
                </Menu>
              ) : (
                <Menu>
                  {this.props.canEdit && (
                    <Menu.Item onClick={() => this.props.duplicate(product)}>
                      {this.props.isDuplicating ? (
                        <span>Duplicating...</span>
                      ) : (
                        <span>Duplicate</span>
                      )}
                    </Menu.Item>
                  )}
                  <PrintProductCheck product={product} />
                  {this.props.canEdit ? (
                    <EditMenuItem product={product} toggleModal={this.props.toggleModal} />
                  ) : (
                    <Menu.Item disabled>
                      <Icon type='stop' /> No edit permissions
                    </Menu.Item>
                  )}
                </Menu>
              )
            }
          >
            <Button icon='ellipsis' style={{ transform: 'rotate(90deg)' }} />
          </Dropdown>
        </div>
      ),
      supplierName: <span>{product.supplierName}</span>,
      tags: (
        <>
          {product.tags && product.tags.length > 0 && (
            <span style={{ whiteSpace: 'nowrap' }}>{product.tags[0].name}</span>
          )}

          {product.tags && product.tags.length > 1 && (
            <div className='blaze-text-muted' style={{ cursor: 'pointer' }}>
              <Popover
                title='Tags'
                placement='right'
                content={
                  <table style={{ width: '5rem' }}>
                    <tbody>
                      {product.tags.map((t) => (
                        <tr key={t.id}>
                          <td>{t.name}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                }
              >
                <span>+{product.tags.length - 1} more</span>
              </Popover>
            </div>
          )}
        </>
      ),
      weight: <span>{product.weight}</span>,
      cannabisWeight: <span>{product.cannabisWeight}</span>,
      cannabisVolume: <span>{product.cannabisVolume}</span>,
      thc: (
        <>
          <span style={{ whiteSpace: 'nowrap' }}>{this.getTHCFormatted(product)}</span>
          {mappedCannabinoidMetaData.size > 0 && (
            <div className='blaze-text-muted' style={{ cursor: 'pointer' }}>
              <Popover
                title='Multi-location THC'
                placement='right'
                content={
                  <table className='w-100'>
                    <tbody>
                      {(this.props.currentCompanyStore!.globalLocations || []).map((location) => {
                        const targetCnb = mappedCannabinoidMetaData.get(location.id)

                        return (
                          <tr key={location.id}>
                            <td>{location.name}</td>
                            <td className='text-right'>
                              {targetCnb
                                ? targetCnb.thcRange
                                : this.getTHCFormatted(product) || 'unset'}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                }
              >
                <span>+{mappedCannabinoidMetaData.size} more</span>
              </Popover>
            </div>
          )}
        </>
      ),
      cbd: (
        <>
          <span style={{ whiteSpace: 'nowrap' }}>{this.getCBDFormatted(product)}</span>
          {mappedCannabinoidMetaData.size > 0 && (
            <div className='blaze-text-muted' style={{ cursor: 'pointer' }}>
              <Popover
                title='Multi-location CBD'
                placement='right'
                content={
                  <table className='w-100'>
                    <tbody>
                      {(this.props.currentCompanyStore!.globalLocations || []).map((location) => {
                        const targetCnb = mappedCannabinoidMetaData.get(location.id)

                        return (
                          <tr key={location.id}>
                            <td>{location.name}</td>
                            <td className='text-right'>
                              {targetCnb
                                ? targetCnb.cbdRange
                                : this.getCBDFormatted(product) || 'unset'}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                }
              >
                <span>+{mappedCannabinoidMetaData.size} more</span>
              </Popover>
            </div>
          )}
        </>
      ),
      strainType: <span>{product.strainType}</span>,
      barcode: <span>{product.barcode}</span>,
      createDate: <span>{moment(product.createDate).format('MMM DD, YYYY')}</span>,
      updateDate: <span>{moment(product.updateDate).format('MMM DD, YYYY')}</span>,
      purchasePrice: (
        <span>
          <Currency centValue={product.purchasePrice} />
        </span>
      ),
      isMasterProduct: product.isMasterProduct,
      companyId: product.companyId,
      id: product.id,
      parentProductId: product.parentProductId,
      isDeleted: product.isDeleted,
    }
  }

  onRowClick = (record: ProductListTableRecord) => {
    if (!this.props.canEdit) {
      return
    }
    if (this.props.showDeleteModal) {
      return
    }

    const product = this.props.products.find((x) => x.id == record.id)

    if (isSystemProduct(product)) {
      return
    }

    const selection = document.getSelection()

    if (selection && selection.type !== 'Range') {
      const productId = record.parentProductId
        ? record.isDeleted
          ? record.id
          : record.parentProductId
        : record.id
      const path = Routes.EDIT_PRODUCT(record.companyId, productId, window.location.search)
      browserHistory.push(path)
    }
  }

  rowComponent = (
    props: JSX.IntrinsicAttributes &
      React.ClassAttributes<HTMLTableRowElement> &
      React.HTMLAttributes<HTMLTableRowElement>,
  ) => {
    if (props) {
      const product = this.props?.products.find((product) => product.id === props?.['data-row-key'])
      return isSystemProduct(product) ? (
        <Tooltip
          overlayClassName='system-products-tooltip'
          placement='top'
          title='System products cannot be edited.'
        >
          <tr {...props} />
        </Tooltip>
      ) : (
        <tr {...props} />
      )
    } else {
      return <tr />
    }
  }

  rowClassName = (record: ProductListTableRecord) => {
    if (!this.props.canEdit) return ''

    const product = this.props.products.find((x) => x.id == record.id)
    if (isSystemProduct(product)) {
      return 'cursor-not-allowed'
    }

    return 'cursor-pointer'
  }

  get initialColumns(): ColumnProps<ProductListTableRecord>[] {
    return [
      { title: 'Image', dataIndex: 'imageUrl', key: 'imageUrl', width: this.IMAGE_WIDTH },
      { title: 'Name', dataIndex: 'name', key: 'name', width: 240 },
      {
        title: 'SKU',
        dataIndex: 'sku',
        key: 'sku',
        width: 'fit-content',
        ellipsis: true,
        className: 'pro-table-col-truncate',
        render: (productSku: string) => (
          <Tooltip
            overlayStyle={{ whiteSpace: 'pre-line', fontSize: '12px' }}
            placement='bottomLeft'
            title={productSku}
          >
            {productSku}
          </Tooltip>
        ),
      },
      { title: 'Category', dataIndex: 'categoryName', key: 'category', width: 140 },
      {
        title: 'Compliance',
        dataIndex: 'complianceCategoryName',
        key: 'complianceCategoryName',
        width: 120,
      },
      { title: 'Price', dataIndex: 'price', key: 'price', width: 100 },
    ]
  }

  get toggleableColumns(): ColumnProps<ProductListTableRecord>[] {
    const extraColumns = [
      {
        title: 'Brand',
        dataIndex: 'supplierName',
        key: 'supplierName',
        width: 160,
      },
      {
        title: 'Tags',
        dataIndex: 'tags',
        key: 'tags',
        width: 100,
        className: 'truncate-column',
        ellipsis: true,
      },
      { title: 'Net product weight', dataIndex: 'weight', key: 'weight', width: 100 },
      { title: 'Cannabis weight', dataIndex: 'cannabisWeight', key: 'cannabisWeight', width: 100 },
      { title: 'Cannabis volume', dataIndex: 'cannabisVolume', key: 'cannabisVolume', width: 100 },
      { title: 'THC', dataIndex: 'thc', key: 'thc', width: 100 },
      { title: 'CBD', dataIndex: 'cbd', key: 'cbd', width: 100 },
      {
        title: 'Strain type',
        dataIndex: 'strainType',
        key: 'strainType',
        width: 160,
      },
      {
        title: 'Barcode',
        dataIndex: 'barcode',
        key: 'barcode',
        width: 160,
        className: 'truncate-column',
        ellipsis: true,
      },
      { title: 'Created at', dataIndex: 'createDate', key: 'createDate', width: 120 },
      { title: 'Updated at', dataIndex: 'updateDate', key: 'updateDate', width: 120 },
      { title: 'Cost', dataIndex: 'purchasePrice', key: 'purchasePrice', width: 100 },
    ]

    return extraColumns.filter((extraColumn) => this.props.extraFields[extraColumn.dataIndex])
  }

  get endColumns(): ColumnProps<ProductListTableRecord>[] {
    return [
      { title: 'Tax', dataIndex: 'taxGroupName', key: 'taxGroupName', width: 100 },
      { title: '', dataIndex: 'gap', key: 'gap' },
      { title: '', dataIndex: 'options', key: 'options', fixed: 'right', width: 48 },
    ]
  }

  get tableDataSource(): ProductListTableRecord[] {
    return this.props.products.map((product) => this.getProductRow(product))
  }

  render() {
    return (
      <Table<ProductListTableRecord>
        columns={[...this.initialColumns, ...this.toggleableColumns, ...this.endColumns]}
        className='intercom-pb-1'
        scroll={{ x: 'max-content' }}
        dataSource={this.tableDataSource}
        pagination={false}
        components={{
          body: {
            row: this.rowComponent,
          },
        }}
        rowClassName={this.rowClassName}
        onRow={(record) => ({
          onClick: () => this.onRowClick(record),
        })}
      />
    )
  }
}
